import { useState, useEffect, createContext, useContext } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";

// Pages
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import InventoryPage from "./pages/InventoryPage";
import VehiclesPage from "./pages/VehiclesPage";
import ProjectsPage from "./pages/ProjectsPage";
import ReparPage from "./pages/ReparPage";
import HistoricoPage from "./pages/HistoricoPage";
import TasksPage from "./pages/TasksPage";
import DailyLogPage from "./pages/DailyLogPage";
import SettingsPage from "./pages/SettingsPage";
import AdminPage from "./pages/AdminPage";
import MessagingPage from "./pages/MessagingPage";
import DynamicCollectionPage from "./pages/DynamicCollectionPage";

// Components
import Layout from "./components/Layout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within AuthProvider");
    }
    return context;
};

// Axios interceptor
axios.interceptors.request.use((config) => {
    const token = localStorage.getItem("token");
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

axios.interceptors.response.use(
    (response) => response,
    (error) => {
        // Si el error es 401 y NO es la página de login
        if (error.response?.status === 401 && !window.location.pathname.includes('/login')) {
            localStorage.removeItem("token");
            localStorage.removeItem("user");
            // window.location.href = "/login"; // Comenta esto un momento para probar
        }
        return Promise.reject(error);
    }
);

// Protected Route
const ProtectedRoute = ({ children }) => {
    const { user, loading } = useAuth();
    
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
                <div className="text-center">
                    <div className="w-12 h-12 border-4 border-[#FFD700] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-[#555555] font-medium">Cargando...</p>
                </div>
            </div>
        );
    }
    
    if (!user) {
        return <Navigate to="/login" replace />;
    }
    
    return children;
};

// Auth Provider
const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const initAuth = async () => {
            const token = localStorage.getItem("token");
            const savedUser = localStorage.getItem("user");
            
            if (token && savedUser) {
                try {
                    const response = await axios.get(`${API}/auth/me`);
                    setUser(response.data);
                    localStorage.setItem("user", JSON.stringify(response.data));
                } catch (error) {
                    localStorage.removeItem("token");
                    localStorage.removeItem("user");
                }
            }
            setLoading(false);
        };
        
        initAuth();
    }, []);

    const login = async (email, password) => {
        try {
            const response = await axios.post(`${API}/auth/login`, { email, password });
            const { token, user: userData } = response.data;
            
            localStorage.setItem("token", token);
            localStorage.setItem("user", JSON.stringify(userData));
            setUser(userData);
            
            toast.success("Sesión iniciada correctamente");
            return { success: true };
        } catch (error) {
            const message = error.response?.data?.detail || "Error al iniciar sesión";
            toast.error(message);
            return { success: false, error: message };
        }
    };

    const register = async (data) => {
        try {
            const response = await axios.post(`${API}/auth/register`, data);
            const { token, user: userData } = response.data;
            
            localStorage.setItem("token", token);
            localStorage.setItem("user", JSON.stringify(userData));
            setUser(userData);
            
            toast.success("Cuenta creada correctamente");
            return { success: true };
        } catch (error) {
            const message = error.response?.data?.detail || "Error al registrarse";
            toast.error(message);
            return { success: false, error: message };
        }
    };

    const updateUser = (newUserData) => {
        setUser(newUserData);
        localStorage.setItem("user", JSON.stringify(newUserData));
    };

    const logout = () => {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        setUser(null);
        toast.success("Sesión cerrada");
    };

    return (
        <AuthContext.Provider value={{ user, loading, login, register, logout, setUser, updateUser }}>
            {children}
        </AuthContext.Provider>
    );
};

function App() {
    return (
        <AuthProvider>
            <BrowserRouter>
                <Routes>
                    <Route path="/login" element={<LoginPage />} />
                    <Route
                        path="/*"
                        element={
                            <ProtectedRoute>
                                <Layout>
                                    <Routes>
                                        <Route path="/" element={<DashboardPage />} />
                                        <Route path="/inventario" element={<InventoryPage />} />
                                        <Route path="/vehiculos" element={<VehiclesPage />} />
                                        <Route path="/obras" element={<ProjectsPage />} />
                                        <Route path="/reparto" element={<ReparPage />} />
                                        <Route path="/historico" element={<HistoricoPage />} />
                                        <Route path="/tareas" element={<TasksPage />} />
                                        <Route path="/diario" element={<DailyLogPage />} />
                                        <Route path="/configuracion" element={<SettingsPage />} />
                                        <Route path="/mensajeria" element={<MessagingPage />} />
                                        <Route path="/admin" element={<AdminPage />} />
                                        {/* Dynamic collection route for custom tables */}
                                        <Route path="/coleccion/:collectionName" element={<DynamicCollectionPage />} />
                                    </Routes>
                                </Layout>
                            </ProtectedRoute>
                        }
                    />
                </Routes>
            </BrowserRouter>
            <Toaster position="top-right" richColors />
        </AuthProvider>
    );
}

export default App;
